import { createStore } from "vuex";
import common from "@/config/common";
import { PaymentIntentResponse } from "@/apiclient/types/PaymentIntentResponse";
import * as settings from "@/config/settings";

function getStored<T>(key: string, fallback: T): T {
  const value = sessionStorage.getItem(key);
  return value ? (JSON.parse(value) as T) : fallback;
}

export default createStore({
  state: {
    showModal: getStored<boolean>(common.storageVariables.showModal, false),
    logoutModal: getStored<boolean>(common.storageVariables.logoutModal, false),
    logoutTimer: getStored<number>(common.storageVariables.logoutTimer, 0),
    clearTheInterval: getStored<number>(
      common.storageVariables.clearTheInterval,
      0
    ),
    modalTitle: getStored<string>(common.storageVariables.modalTitle, ""),
    modalContent: getStored<string>(common.storageVariables.modalContent, ""),
    uploadFiles: getStored<any[]>(common.storageVariables.uploadFiles, []),
    uploadProgress: getStored<any[]>(
      common.storageVariables.uploadProgress,
      []
    ),
    uploadedFiles: getStored<number>(common.storageVariables.uploadedFiles, 0),
    totalFiles: getStored<number>(common.storageVariables.totalFiles, 0),
    cancelTokenSource: getStored<string>(
      common.storageVariables.cancelTokenSource,
      ""
    ),
    documentSubmitBtn: getStored<string>(
      common.storageVariables.documentSubmitBtn,
      "SKIP & NEXT"
    ),
    benefitAmount: getStored<any[]>(common.storageVariables.benefitAmount, []),
    isFormDirty:
      getStored<string>(common.storageVariables.formDirty, "false") === "true",
    autoLogoutMsg: getStored<string>(common.storageVariables.autoLogoutMsg, ""),
    documentsUploaded: getStored<any[]>(
      common.storageVariables.documentsUploaded,
      []
    ),
    benefitRates: getStored<object>(common.storageVariables.benefitRates, {}),
    enrollment: getStored<object>(common.storageVariables.enrollment, {}),
    benefits: getStored<any[]>(common.storageVariables.benefits, []),
    dependents: getStored<any[]>(common.storageVariables.dependents, []),
    validationMsg: getStored<string>(common.storageVariables.validationMsg, ""),
    calculateRates: getStored<any[]>(
      common.storageVariables.calculateRates,
      []
    ),
    totalEnrollments: getStored<number>(
      common.storageVariables.totalEnrollments,
      0
    ),
    wasSpecialOpenEnrollmentCreated: getStored<number | null>(
      common.storageVariables.wasSpecialOpenEnrollmentCreated,
      null
    ),
    maskedEmailAddress: getStored<number | null>(
      common.storageVariables.maskedEmailAddress,
      null
    ),
    isPayed: getStored<boolean>(common.storageVariables.isPayed, false),
    paymentIntents: getStored<PaymentIntentResponse[]>(
      common.storageVariables.paymentIntents,
      []
    ),
    settings: getStored<settings.Settings>(
      common.storageVariables.settings, 
      settings.default
    )
  },

  getters: {
    getShowModal(state) {
      return state.showModal;
    },
    getLogoutTimer(state) {
      return state.logoutTimer;
    },
    getLogoutModal(state) {
      return state.logoutModal;
    },
    getClearTheInterval(state) {
      return state.clearTheInterval;
    },
    getModalTitle(state) {
      return state.modalTitle;
    },
    getModalContent(state) {
      return state.modalContent;
    },
    getUploadFiles(state) {
      return state.uploadFiles;
    },
    getUploadProgress(state) {
      return state.uploadProgress;
    },
    getUploadedFiles(state) {
      return state.uploadedFiles;
    },
    getTotalFiles(state) {
      return state.totalFiles;
    },
    getCancelTokenSource(state) {
      return state.cancelTokenSource;
    },
    getDocumentSubmitBtn(state) {
      return state.documentSubmitBtn;
    },
    getBenefitAmount(state) {
      return state.benefitAmount;
    },
    getFormDirty(state) {
      return state.isFormDirty;
    },
    getAutoLogoutMsg(state) {
      return state.autoLogoutMsg;
    },
    getDocumentsUploaded(state) {
      return state.documentsUploaded;
    },
    getBenefitRates(state) {
      return state.benefitRates;
    },
    getEnrollment(state) {
      return state.enrollment;
    },
    getTotalEnrollments(state) {
      return state.totalEnrollments;
    },
    getBenefits(state) {
      return state.benefits;
    },
    getDependents(state) {
      return state.dependents;
    },
    getValidationMsg(state) {
      return state.validationMsg;
    },
    getCalculateRates(state) {
      return state.calculateRates;
    },
    getWasSpecialOpenEnrollmentCreated(state) {
      return state.wasSpecialOpenEnrollmentCreated;
    },
    getMaskedEmailAddress(state) {
      return state.maskedEmailAddress;
    },
    getIsPayed(state) {
      return state.isPayed;
    },
    getPaymentIntents(state) {
      return state.paymentIntents;
    },
    getSettings(state) {
      return state.settings;
    }
  },

  mutations: {
    setShowModal(state, data) {
      sessionStorage.setItem(
        common.storageVariables.showModal,
        JSON.stringify(data)
      );
      state.showModal = data;
    },
    setLogoutTimer(state) {
      const newValue = state.logoutTimer + 1;
      sessionStorage.setItem(
        common.storageVariables.logoutTimer,
        JSON.stringify(newValue)
      );
      state.logoutTimer = newValue;
    },
    setLogoutModal(state, data) {
      sessionStorage.setItem(
        common.storageVariables.logoutModal,
        JSON.stringify(data)
      );
      state.logoutModal = data;
    },
    setClearTheInterval(state) {
      const newValue = state.clearTheInterval + 1;
      sessionStorage.setItem(
        common.storageVariables.clearTheInterval,
        JSON.stringify(newValue)
      );
      state.clearTheInterval = newValue;
    },
    setModalTitle(state, data) {
      sessionStorage.setItem(
        common.storageVariables.modalTitle,
        JSON.stringify(data)
      );
      state.modalTitle = data;
    },
    setModalContent(state, data) {
      sessionStorage.setItem(
        common.storageVariables.modalContent,
        JSON.stringify(data)
      );
      state.modalContent = data;
    },
    setUploadFile(state, data) {
      state.uploadFiles.push(data);
      sessionStorage.setItem(
        common.storageVariables.uploadFiles,
        JSON.stringify(state.uploadFiles)
      );
    },
    removeUploadFile(state, index) {
      state.uploadFiles[index].show = false;
      state.uploadFiles[index].disabled = true;
      sessionStorage.setItem(
        common.storageVariables.uploadFiles,
        JSON.stringify(state.uploadFiles)
      );
    },
    removeUploadFiles(state) {
      state.uploadFiles = [];
      sessionStorage.setItem(
        common.storageVariables.uploadFiles,
        JSON.stringify(state.uploadFiles)
      );
    },
    setUploadProgress(state, data) {
      if (!state.uploadProgress[data.index]) {
        state.uploadProgress.push({ progress: data.progress, time: data.time });
      } else {
        state.uploadProgress[data.index] = {
          progress: data.progress,
          time: data.time,
        };
      }
      sessionStorage.setItem(
        common.storageVariables.uploadProgress,
        JSON.stringify(state.uploadProgress)
      );
    },
    removeUploadProgress(state, data) {
      state.uploadProgress.splice(data, 1);
      sessionStorage.setItem(
        common.storageVariables.uploadProgress,
        JSON.stringify(state.uploadProgress)
      );
    },
    setUploadedFiles(state, data) {
      sessionStorage.setItem(
        common.storageVariables.uploadedFiles,
        JSON.stringify(data)
      );
      state.uploadedFiles = data;
    },
    setTotalFiles(state, data) {
      sessionStorage.setItem(
        common.storageVariables.totalFiles,
        JSON.stringify(data)
      );
      state.totalFiles = data;
    },
    setCancelTokenSource(state, data) {
      sessionStorage.setItem(
        common.storageVariables.cancelTokenSource,
        JSON.stringify(data)
      );
      state.cancelTokenSource = data;
    },
    setDocumentSubmitBtn(state, data) {
      sessionStorage.setItem(
        common.storageVariables.documentSubmitBtn,
        JSON.stringify(data)
      );
      state.documentSubmitBtn = data;
    },
    setBenefitAmount(state, data) {
      sessionStorage.setItem(
        common.storageVariables.benefitAmount,
        JSON.stringify(data)
      );
      state.benefitAmount = data;
    },
    setFormDirty(state, data) {
      sessionStorage.setItem(
        common.storageVariables.formDirty,
        JSON.stringify(data)
      );
      state.isFormDirty = data;
    },
    setAutoLogoutMsg(state, data) {
      sessionStorage.setItem(
        common.storageVariables.autoLogoutMsg,
        JSON.stringify(data)
      );
      state.autoLogoutMsg = data;
    },
    setDocumentsUploaded(state, data) {
      sessionStorage.setItem(
        common.storageVariables.documentsUploaded,
        JSON.stringify(data)
      );
      state.documentsUploaded = data;
    },
    setBenefitRates(state, data) {
      sessionStorage.setItem(
        common.storageVariables.benefitRates,
        JSON.stringify(data)
      );
      state.benefitRates = data;
    },
    setEnrollment(state, data) {
      sessionStorage.setItem(
        common.storageVariables.enrollment,
        JSON.stringify(data)
      );
      state.enrollment = data;
    },
    setTotalEnrollments(state, data) {
      sessionStorage.setItem(
        common.storageVariables.totalEnrollments,
        JSON.stringify(data)
      );
      state.totalEnrollments = data;
    },
    setBenefits(state, data) {
      sessionStorage.setItem(
        common.storageVariables.benefits,
        JSON.stringify(data)
      );
      state.benefits = data;
    },
    setDependents(state, data) {
      sessionStorage.setItem(
        common.storageVariables.dependents,
        JSON.stringify(data)
      );
      state.dependents = data;
    },
    setValidationMsg(state, data) {
      sessionStorage.setItem(
        common.storageVariables.validationMsg,
        JSON.stringify(data)
      );
      state.validationMsg = data;
    },
    setCalculateRates(state, data) {
      sessionStorage.setItem(
        common.storageVariables.calculateRates,
        JSON.stringify(data)
      );
      state.calculateRates = data;
    },
    setWasSpecialOpenEnrollmentCreated(state, data) {
      sessionStorage.setItem(
        common.storageVariables.wasSpecialOpenEnrollmentCreated,
        JSON.stringify(data)
      );

      state.wasSpecialOpenEnrollmentCreated = data;
    },
    setMaskedEmailAddress(state, data) {
      sessionStorage.setItem(
        common.storageVariables.maskedEmailAddress,
        JSON.stringify(data)
      );

      state.maskedEmailAddress = data;
    },
    setIsPayed(state, data) {
      sessionStorage.setItem(
        common.storageVariables.isPayed,
        JSON.stringify(data)
      );

      state.isPayed = data;
    },
    setPaymentIntents(state, data) {
      sessionStorage.setItem(
        common.storageVariables.paymentIntents,
        JSON.stringify(data)
      );

      state.paymentIntents = data;
    },
    setSettings(state, settings: settings.Settings) {
      sessionStorage.setItem(
        common.storageVariables.settings,
        JSON.stringify(settings)
      );
      
      state.settings = settings;
    }
  },

  actions: {},

  modules: {},
});
